/** Formatting helpers, ported from design/blueprint/app.js. */

export function fmtEUR(n: number | null | undefined): string {
  if (n == null) return "-";
  return "€ " + Math.round(n).toLocaleString("de-DE");
}

/** Cost, in the unit the budget is kept in. One credit is one euro; naming
 *  it once, in one place, keeps the two from drifting apart on screen. */
export function fmtCredits(n: number | null | undefined): string {
  if (n == null) return "-";
  const value = n.toLocaleString("de-DE", {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  });
  return `${value} Credits`;
}

export function fmtEURk(n: number | null | undefined): string {
  if (n == null) return "-";
  if (n >= 1_000_000) return "€ " + (n / 1_000_000).toFixed(1).replace(".", ",") + " Mio.";
  if (n >= 1_000) return "€ " + Math.round(n / 1_000) + " Tsd.";
  return fmtEUR(n);
}

/** Quellenbeschriftung: immer der Host der Fundstelle (ec.europa.eu,
 *  ted.europa.eu, …), nie der Name des Suchwegs — „Tavily" ist keine Quelle. */
export function hostLabel(href: string | null | undefined): string {
  if (!href) return "-";
  try {
    return new URL(href).hostname.replace(/^www\./, "");
  } catch {
    return href.slice(0, 40);
  }
}

export function fmtDate(iso: string | null | undefined): string {
  if (!iso) return "-";
  return new Date(iso).toLocaleDateString("de-DE", {
    day: "2-digit",
    month: "2-digit",
    year: "numeric",
  });
}

export function fmtDateTime(iso: string | null | undefined): string {
  if (!iso) return "-";
  const d = new Date(iso);
  return (
    d.toLocaleDateString("de-DE", { day: "2-digit", month: "2-digit", year: "numeric" }) +
    " · " +
    d.toLocaleTimeString("de-DE", { hour: "2-digit", minute: "2-digit" })
  );
}

export function urgencyToken(days: number | null | undefined): string {
  if (days == null || days < 0) return "--text-muted";
  if (days < 7) return "--urgency-0";
  if (days < 30) return "--urgency-1";
  if (days < 90) return "--urgency-2";
  if (days < 180) return "--urgency-3";
  return "--urgency-far";
}

export function urgencyLabel(days: number | null | undefined): string {
  if (days == null) return "-";
  if (days < 0) return "abgelaufen";
  return `${days} Tage`;
}

export const STATUS_META: Record<string, { label: string; cls: string }> = {
  open: { label: "Offen", cls: "st-open" },
  closing_soon: { label: "Läuft bald ab", cls: "st-closing" },
  forthcoming: { label: "Demnächst", cls: "st-forthcoming" },
  closed: { label: "Geschlossen", cls: "st-closed" },
  cancelled: { label: "Eingestellt", cls: "st-closed" },
};

export const STEP_LABELS: Record<string, string> = {
  plan: "Plan",
  collect: "Sammeln",
  enrich: "Anreichern",
  extract: "Extrahieren",
  match: "Abgleichen",
  score: "Bewerten",
  report: "Bericht",
  export: "Export",
};

export const RUN_STATE_LABELS: Record<string, string> = {
  succeeded: "Erfolgreich",
  partial: "Teilweise",
  failed: "Fehlgeschlagen",
  running: "Läuft",
  queued: "Wartet",
  budget_exceeded: "Budget überschritten",
  cancelled: "Abgebrochen",
  skipped: "Übersprungen",
};

export function runStateColor(status: string): string {
  if (status === "succeeded") return "var(--status-ok)";
  if (status === "failed" || status === "budget_exceeded") return "var(--status-error)";
  return "var(--status-warning)";
}

// issuerShort() aus app.js ist entfallen: Der Geber-Chip auf den Karten
// stützte sich auf eine Stichwortliste der Blaupausen-Mockdaten und zeigte
// bei echten Treffern das erste Wort des Organisationsnamens
// („KOMENDA", „Ministerstvo") — mehr Rauschen als Information.
// Programm und Organisation stehen als Kartenfelder zur Verfügung.
